import * as React from 'react';
import keyBy from 'lodash/keyBy';
import Box from '../box';
import Typography from '../typography';
import { palette } from '../../styles';

import {
	Root,
	Segment,
	SegmentLayout,
} from './styled';


export type SegmentedListProps<T extends Object = any> = {
	value: Array<T | string | number>;
	dataSourceConfig?: {
		value: string;
		text: string;
	};
	dataSource: Array<T>;
	errorText?: React.ReactNode;
	multiple?: boolean;
	selectAllItemsID?: string;
	disabled?: boolean;
	transformValues: (dataSource: Array<T>, getID: (item: T) => number | string, IDs: Array<string | number>) => Array<T>;
	onChange: (e: React.SyntheticEvent<{}>, value: Array<T>) => void;
	renderSegment: (options: RenderSegmentOptions<T>) => React.ReactNode;
};

type RenderSegmentOptions<T> = {
	item: T;
	key: string | number;
	isActive: boolean;
	disabled: boolean;
};

class SegmentedList<T> extends React.PureComponent<SegmentedListProps<T>> {
	static displayName = 'SegmentedList';
	static defaultProps: Partial<SegmentedListProps> = {
		dataSourceConfig: {
			value: 'value',
			text: 'text',
		},
	};

	getID = (item: T) => item[this.props.dataSourceConfig.value];

	getText = (item: T) => item[this.props.dataSourceConfig.text];

	handleClick = (item: T) => (e: React.SyntheticEvent<{}>) => {
		const {
			value,
			multiple,
			selectAllItemsID,
			dataSource,
			transformValues,
			onChange,
		} = this.props;
		let list = [];
		const valueList = (typeof transformValues === 'function'
			? transformValues(dataSource, this.getID, value as Array<string | number>)
			: value) as Array<T>;

		if (multiple) {
			const ID = this.getID(item);
			const valueIDs = valueList.map(x => this.getID(x));
			const hasSameValue = valueIDs.includes(ID);

			if (ID === selectAllItemsID) {
				if (hasSameValue) {
					list = [];
				} else {
					list = [...dataSource];
				}
			} else {
				if (hasSameValue) {
					list = valueList.filter(x => this.getID(x) !== ID && this.getID(x) !== selectAllItemsID);
				} else {
					list = [...valueList, item];

					if (typeof selectAllItemsID !== 'undefined' && list.length === dataSource.length - 1 && list.every(x => this.getID(x) !== selectAllItemsID)) {
						const selectAllItem = dataSource.find(x => this.getID(x) === selectAllItemsID);

						selectAllItem && list.push(selectAllItem);
					}
				}
			}
		} else {
			list = [item];
		}

		onChange(e, list);
	};

	renderSegment = (options: RenderSegmentOptions<T>) => {
		const {
			item,
			key,
			isActive,
			disabled,
		} = options;

		return (
			<SegmentLayout key={key}>
				<Segment
					isActive={isActive}
					disabled={disabled}
					onClick={this.handleClick(item)}>
					{this.getText(item)}
				</Segment>
			</SegmentLayout>
		);
	};

	render() {
		const {
			value,
			dataSource,
			errorText,
			transformValues,
			disabled,
			renderSegment,
		} = this.props;
		const valueList = (typeof transformValues === 'function'
			? transformValues(dataSource, this.getID, value as Array<string | number>)
			: value) as Array<T>;

		const valueIDs = valueList.map(x => this.getID(x));

		return (
			<Root>
				{
					dataSource.map(x => {
						const ID = this.getID(x);
						const isActive = valueIDs.includes(ID); 

						return typeof renderSegment === 'function'
							? renderSegment({
									item: x,
									key: this.getID(x),
									isActive,
									disabled,
								})
							: this.renderSegment({
									item: x,
									key: this.getID(x),
									isActive,
									disabled,
								})
					})
				}
				{
					errorText &&
					<Box marginTop='2px'>
						<Typography.Text fontSize={12} color={palette.dangerPrimary}>{errorText}</Typography.Text>
					</Box>
				}
			</Root>
		);
	}
}

function transformSegmentedListValues<T>(dataSource: Array<T>, getID: (item: T) => number | string, IDs: Array<string | number>) {
	const dataSourceMap = keyBy(dataSource, x => getID(x));
	const value = IDs.map(x => dataSourceMap[x]).filter(Boolean);

	return value;
}

export type DataSourceItem = {
	value: string | number;
	text: string;
};


export { transformSegmentedListValues };
export default SegmentedList;
